import React from 'react';
import { Formik, getIn, useFormikContext } from 'formik';
import { isNil, snakeCase } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import CheckboxFormik from '~/components/CheckboxFormik';
import { Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CancelButton from '~/components/core/Buttons/CancelButton';
import FieldConfigurationCard from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/FieldConfigurationCard/FieldConfigurationCard';
import TextFieldFormik, { useSetDefaultFieldsOnChange } from '~/components/TextFieldFormik';
import type { DATypeConfigInfo } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

const FORMIK_IDS = {
  KEY: 'key',
  DISPLAY_NAME: 'display_name',
  CONFIG: 'config',
};

const PREDEFINED_FIELD_IDS = ['coverage_decision', 'covered_amount', 'payable_type', 'explanation'];

const ALLOWED_PAYABLE_TYPES_OPTIONS = [
  { value: 'indemnity', label: 'Indemnity' },
  { value: 'expenses', label: 'Expenses' },
  { value: 'both', label: 'Both (per-line selection)' },
];

const DEFAULT_CONFIG = {
  enable_currency_conversion: true,
  enable_depreciation: false,
  enable_make_payment: true,
  allowed_payable_types: 'both',
  predefined_fields: {
    coverage_decision: { desc: 'Coverage Decision', active: true, mandatory: false, type: 'select', dynamic: true },
    covered_amount: { desc: 'Covered Amount', active: true, mandatory: false, type: 'monetary', dynamic: true },
    payable_type: { desc: 'Payable Type', active: true, mandatory: false, type: 'select', dynamic: true },
    explanation: { desc: 'Explanation', active: true, mandatory: false, type: 'string', dynamic: true },
  },
};

interface UpsertDamageAssessmentTypeDialogProps {
  damageAssessmentTypes: DATypeConfigInfo[];
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  damageAssessmentType?: DATypeConfigInfo;
}

const UpsertDamageAssessmentTypeDialogInner: React.FC<{ isUpdateMode: boolean }> = ({ isUpdateMode }) => {
  const { isSubmitting, values } = useFormikContext<DATypeConfigInfo>();

  useSetDefaultFieldsOnChange(getIn(values, FORMIK_IDS.DISPLAY_NAME), {
    [FORMIK_IDS.KEY]: isUpdateMode ? getIn(values, FORMIK_IDS.KEY) : snakeCase(getIn(values, FORMIK_IDS.DISPLAY_NAME)),
  });

  return (
    <div className="grid gap-20">
      <div>
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
          Details
        </Text>
        <div className="grid grid-cols-2 gap-20">
          <TextFieldFormik id={FORMIK_IDS.DISPLAY_NAME} label="Name" disabled={isSubmitting} />
          <TextFieldFormik id={FORMIK_IDS.KEY} label="Key" disabled={isSubmitting || isUpdateMode} />
        </div>
      </div>
      <div>
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
          Feature Toggles
        </Text>
        <div className="grid grid-cols-3 gap-20">
          <CheckboxFormik id="config.enable_currency_conversion" label="Currency Conversion" />
          <CheckboxFormik id="config.enable_depreciation" label="Depreciation" />
          <CheckboxFormik id="config.enable_make_payment" label="Make Payment" />
        </div>
        <div className="mt-20 grid grid-cols-3 gap-20">
          <TextFieldFormik
            id="config.allowed_payable_types"
            label="Payable Types"
            select
            disabled={isSubmitting}
            fullWidth
          >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            {ALLOWED_PAYABLE_TYPES_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'none'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextFieldFormik>
        </div>
      </div>
      <div>
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
          Assessment Predefined Fields
        </Text>
        {PREDEFINED_FIELD_IDS.map((fieldId) => (
          <FieldConfigurationCard
            key={fieldId}
            idPrefix="config.predefined_fields"
            fieldId={fieldId}
            disableType
            disableCategories
            withoutRequiredForClaimClosing
            withRequiredConfirmationModal={false}
          />
        ))}
      </div>
    </div>
  );
};

const UpsertDamageAssessmentTypeDialog: React.FC<UpsertDamageAssessmentTypeDialogProps> = ({
  damageAssessmentTypes,
  onClose,
  onSubmit,
  damageAssessmentType,
}) => {
  return (
    <Formik
      initialValues={{
        [FORMIK_IDS.DISPLAY_NAME]: damageAssessmentType?.display_name ?? '',
        [FORMIK_IDS.KEY]: damageAssessmentType?.key ?? '',
        [FORMIK_IDS.CONFIG]: {
          ...DEFAULT_CONFIG,
          ...damageAssessmentType?.config,
          allowed_payable_types: damageAssessmentType?.config?.allowed_payable_types ?? 'both',
          predefined_fields: {
            ...DEFAULT_CONFIG.predefined_fields,
            ...damageAssessmentType?.config?.predefined_fields,
          },
        },
      }}
      validationSchema={Yup.object().shape({
        [FORMIK_IDS.DISPLAY_NAME]: Yup.string()
          .required('Required')
          .trim()
          .lowercase()
          .max(256)
          .notOneOf(
            damageAssessmentTypes
              .filter((r) => r.id !== damageAssessmentType?.id)
              .map((r) => r.display_name.toLowerCase().trim()),
            'Must be unique'
          ),
        [FORMIK_IDS.KEY]: Yup.string()
          .required('Required')
          .trim()
          .max(128)
          .matches(/^[a-z0-9_]*$/, 'Can only contain lowercase letters, numbers and underscores')
          .notOneOf(
            damageAssessmentTypes.filter((r) => r.id !== damageAssessmentType?.id).map((r) => r.key),
            'Must be unique'
          ),
      })}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        try {
          await onSubmit(values);
          onClose();
        } catch (error) {
          formikProps.setSubmitting(false);
          await reportAxiosError(error);
        }
      }}
    >
      {({ isSubmitting, handleSubmit }) => (
        <CardDialog
          title={damageAssessmentType ? 'Edit Damage Assessment Type' : 'Create Damage Assessment Type'}
          isDialog
          onClose={onClose}
          fullWidth
          maxWidth="lg"
        >
          <UpsertDamageAssessmentTypeDialogInner isUpdateMode={!isNil(damageAssessmentType)} />
          <div className="mt-px mt-32 flex w-full justify-end">
            <CancelButton disabled={isSubmitting} onClick={onClose} />
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
              Save
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default UpsertDamageAssessmentTypeDialog;
